import { ChangeEvent, FC, KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Input, IInputProps } from "../Input";
import { isHebrewText } from "../../../utils/isHebrewText";
import "./styles.scss";

/**
 * A place object shaped like the legacy `google.maps.places.PlaceResult` that
 * the old `<Autocomplete>` widget produced. We keep this shape so existing
 * call-site handlers (`handleCoordinates`, `onPlaceChanged`) — which read
 * `place.geometry.location.lat()/lng()`, `place.address_components[].long_name`
 * / `.types[0]`, and `place.place_id` — keep working unchanged after the swap
 * to the modern, non-deprecated Places API.
 */
export interface LegacyPlace {
  formatted_address?: string;
  place_id?: string;
  geometry?: { location?: google.maps.LatLng };
  address_components?: Array<{ long_name: string; short_name: string; types: string[] }>;
}

interface Suggestion {
  placeId: string;
  primary: string;
  secondary: string;
}

interface Props extends Omit<IInputProps, "onChange"> {
  /** Passthrough for manual typing (call-site invalidates coords, etc.). */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Fired when the user picks a suggestion, with a legacy-shaped place. */
  onPlaceSelected: (place: LegacyPlace) => void;
  /** Primary place types to restrict suggestions to (modern API). */
  includedPrimaryTypes?: string[];
  /** ISO-3166 region/country codes to restrict suggestions to (e.g. ["il"]). */
  includedRegionCodes?: string[];
  /**
   * Restricts suggestions to a bounding box (e.g. a US state's viewport) —
   * unlike `includedRegionCodes` (country-level only), this narrows results
   * within a single country. Suggestions entirely outside the box are
   * excluded, not just biased.
   */
  locationRestriction?: google.maps.LatLngBoundsLiteral;
  /** Debounce before requesting predictions, in ms. */
  debounceMs?: number;
  /**
   * When true, focusing the input fetches suggestions for the existing value so
   * the user can re-pick a Google match without having to delete or retype. This
   * matters for imported addresses that Google only matched approximately (they
   * carry town-level coordinates/place_id, so there is no reliable per-address
   * "unverified" flag to gate on). No-op when the field is empty.
   */
  suggestOnFocus?: boolean;
  /**
   * Optional escape hatch to render a custom input (e.g. one with a "use my
   * location" button) instead of the default base `<Input>`. Must wire the
   * provided `value` + `onChange` (and, ideally, `onFocus`) onto its input so
   * typing/focusing fetches suggestions.
   */
  renderInput?: (args: {
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
  }) => ReactNode;
  /** Caller-supplied label for the "save as text only" row. No hardcoded English in this file. */
  textOnlyLabel?: string;
  /** Fired when the user picks the "save as text only" row, with the currently typed text. */
  onSaveAsTextOnly?: (typedText: string) => void;
}

/**
 * Drop-in replacement for the deprecated `@react-google-maps/api`
 * `<Autocomplete>` widget. Renders our own `<Input>` (so it looks identical to
 * the rest of the app) plus a styled suggestions dropdown, powered by the
 * modern `google.maps.places.AutocompleteSuggestion` + `Place.fetchFields`
 * APIs. See MD/PLACES_AUTOCOMPLETE_MIGRATION_PLAN.md.
 *
 * Degrades gracefully to a plain input if the modern API isn't present (older
 * Maps JS channel) — no suggestions, but typing still works.
 */
export const PlaceAutocompleteInput: FC<Props> = ({
  onChange,
  onPlaceSelected,
  includedPrimaryTypes,
  includedRegionCodes,
  locationRestriction,
  debounceMs = 250,
  suggestOnFocus,
  renderInput,
  textOnlyLabel,
  onSaveAsTextOnly,
  ...inputProps
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Fixed-position rect for the portalled dropdown (computed from the input).
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const sessionTokenRef = useRef<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The text that produced the current suggestion list, so selectSuggestion can
  // decide its own request language from the same typed text (Hebrew vs. Latin)
  // rather than a caller-supplied/account-derived language.
  const lastQueryRef = useRef<string>("");

  const placesLib = (): any => (window as any).google?.maps?.places;

  // One session token spans a series of keystrokes + the resulting selection
  // (Google bills per session, not per keystroke). Reset after each selection.
  const ensureToken = () => {
    const places = placesLib();
    if (!places?.AutocompleteSessionToken) return undefined;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const fetchSuggestions = useCallback(
    async (input: string) => {
      const places = placesLib();
      const trimmed = input.trim();
      if (trimmed.length < 3) {
        // Fewer than 3 characters is too little to search meaningfully — skip the request
        // (cost + flicker) and just close any stale dropdown from a longer prior value.
        setSuggestions([]);
        setOpen(false);
        return;
      }
      if (!places?.AutocompleteSuggestion) {
        // Google's Places library isn't available (not loaded / blocked) — no suggestions,
        // but the user has typed real text, so still open the dropdown to offer "save as
        // text only" rather than silently stranding them with no way to save the address.
        setSuggestions([]);
        setOpen(true);
        return;
      }
      try {
        const request: any = { input, sessionToken: ensureToken() };
        if (includedPrimaryTypes?.length) request.includedPrimaryTypes = includedPrimaryTypes;
        if (includedRegionCodes?.length) {
          request.includedRegionCodes = includedRegionCodes.map((c) => c.toLowerCase());
        }
        if (locationRestriction) request.locationRestriction = locationRestriction;
        // Per-request language decided from the text the user actually typed —
        // NOT from workspace/account country — so an English-typed query never
        // comes back overwritten with Hebrew results (and vice versa).
        //
        // Set EXPLICITLY in both directions. Omitting it for a Latin query did
        // not mean "no preference": it fell through to the language the Maps
        // script was loaded in, which is the UI language. Typing "1414" in a
        // Hebrew-locale session returned predictions whose street line stayed
        // English but whose locality was translated —
        // "14149 Player Street / יוסטון, טקסס, ארצות הברית" — one suggestion in
        // two languages. Same omission, same cause, as the place-DETAILS
        // request in selectSuggestion below.
        request.language = isHebrewText(input) ? "iw" : "en";
        lastQueryRef.current = input;
        const { suggestions: raw } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        const mapped: Suggestion[] = (raw ?? [])
          .map((s: any) => s.placePrediction)
          .filter(Boolean)
          .map((pp: any) => ({
            placeId: pp.placeId,
            primary: pp.mainText?.text ?? pp.text?.text ?? "",
            secondary: pp.secondaryText?.text ?? "",
          }));
        setSuggestions(mapped);
        // Open whenever the request succeeded and the input isn't empty — even with
        // zero suggestions — so the "save as text only" row (rendered below, gated
        // separately on `showTextOnlyOption`) can still be shown in an otherwise-empty
        // dropdown. Real suggestion rows still only render when `suggestions.length > 0`.
        setOpen(true);
        setActiveIndex(-1);
      } catch {
        // The Places request itself failed (network/quota) — same reasoning as the
        // library-unavailable branch above: still offer "save as text only".
        setSuggestions([]);
        setOpen(true);
      }
    },
    [includedPrimaryTypes, includedRegionCodes, locationRestriction] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e); // manual-typing passthrough
    const val = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), debounceMs);
  };

  // Clicking/tabbing into a field that still holds an unverified address should
  // surface Google's options for that text right away — without the user having
  // to delete characters or retype to trigger a fetch.
  const handleFocus = () => {
    if (!suggestOnFocus) return;
    const val = (inputProps.value ?? "").toString();
    if (!open && val.trim()) fetchSuggestions(val);
  };

  const selectSuggestion = async (s: Suggestion) => {
    const places = placesLib();
    setOpen(false);
    setSuggestions([]);
    try {
      // Explicit BOTH ways. Leaving this undefined for a Latin-typed query did
      // not mean "no preference" — it meant "whatever language the Maps script
      // was loaded in", which is the app's UI language. So a Hebrew-locale user
      // typing "1414 President St, Brooklyn" got the place back as
      // "1414 President St, Brooklyn, NY 11213, ארצות הברית" and that is what
      // was stored. Ask for the language the query was actually written in.
      // (The country stored alongside no longer depends on this at all — see
      // countryEnglishFrom in utils/placeComponents.ts, which derives it from
      // the non-localised ISO code. This keeps the *displayed* address honest.)
      const requestedLanguage = isHebrewText(lastQueryRef.current) ? "iw" : "en";
      const place = new places.Place(
        { id: s.placeId, requestedLanguage }
      );
      await place.fetchFields({
        fields: ["formattedAddress", "location", "addressComponents", "id"],
      });
      const legacy: LegacyPlace = {
        formatted_address: place.formattedAddress ?? undefined,
        place_id: place.id ?? s.placeId,
        geometry: { location: place.location ?? undefined },
        address_components: (place.addressComponents ?? []).map((c: any) => ({
          long_name: c.longText ?? "",
          short_name: c.shortText ?? "",
          types: c.types ?? [],
        })),
      };
      onPlaceSelected(legacy);
    } catch (err) {
      // fetchFields can fail (network/quota/an unexpected requestedLanguage value)
      // — a suggestion the user explicitly clicked must never be silently dropped
      // (that used to leave place_id unset and the "select an address from the
      // suggestions" validation error stuck forever). Fall back to a minimal
      // LegacyPlace built from the suggestion itself so place_id is always set.
      console.error("PlaceAutocompleteInput: fetchFields failed, using suggestion fallback", err);
      onPlaceSelected({
        place_id: s.placeId,
        formatted_address: s.secondary ? `${s.primary}, ${s.secondary}` : s.primary,
      });
    } finally {
      // A selection ends the billing session; next keystroke starts a fresh one.
      sessionTokenRef.current = null;
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Position the portalled dropdown directly under the input, in fixed
  // coordinates read from the input's bounding rect.
  const updatePosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  // While open, keep the dropdown glued to the input as the page / any
  // scrollable ancestor (e.g. a modal body) scrolls or the window resizes.
  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollResize = () => updatePosition();
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open, updatePosition]);

  // Close the dropdown on outside click. The list is portalled to <body>, so it
  // is NOT inside containerRef — check both the input container and the list.
  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  // The "save as text only" row must be offered any time the user has typed ANYTHING —
  // even plain text with no house number — regardless of whether Google found 0, some, or
  // many suggestions (or errored). Recomputed on each render since `inputProps.value` is a
  // controlled prop owned by the parent form, not local state.
  const currentTypedValue = (inputProps.value ?? "").toString();
  const showTextOnlyOption = currentTypedValue.trim().length > 2;

  const shouldShowDropdown =
    !!menuRect && open && (suggestions.length > 0 || (showTextOnlyOption && !!onSaveAsTextOnly && !!textOnlyLabel));

  return (
    <div className="place-autocomplete" ref={containerRef} onKeyDown={handleKeyDown}>
      {renderInput
        ? renderInput({ value: inputProps.value, onChange: handleInputChange, onFocus: handleFocus })
        : <Input {...inputProps} onChange={handleInputChange} onFocus={handleFocus} />}
      {shouldShowDropdown &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="place-autocomplete__list"
            // Focusable so a keyboard user can scroll a long list (axe scrollable-region-focusable); the input keeps the arrow-key control.
            tabIndex={0}
            style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
          >
            {suggestions.map((s, i) => (
              <li
                key={s.placeId}
                className={`place-autocomplete__item ${i === activeIndex ? "is-active" : ""}`}
                // onMouseDown (not onClick) so it fires before the input's blur,
                // which would otherwise close the dropdown first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(s);
                }}
              >
                <span className="place-autocomplete__item-primary">{s.primary}</span>
                {s.secondary && (
                  <span className="place-autocomplete__item-secondary">{s.secondary}</span>
                )}
              </li>
            ))}
            {onSaveAsTextOnly && textOnlyLabel && showTextOnlyOption && (
              <li
                className="place-autocomplete__item place-autocomplete__item--text-only"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  onSaveAsTextOnly(currentTypedValue);
                }}
              >
                {textOnlyLabel}
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};
